/**
 * Pure helpers for click arbitration hit-testing on a MapLibre map.
 */

import type maplibregl from "maplibre-gl";

import type { MapClickHit } from "./mapClick.types";

/**
 * Builds the layer-id to paint-order-index lookup for the current style.
 *
 * @param map Active MapLibre map.
 * @returns Map of layer id to its paint index (higher = painted on top).
 */
export function buildLayerZIndex( map: maplibregl.Map ): Map<string, number> {
    const order = map.getLayersOrder();

    return new Map( order.map( ( layerId, index ) => [ layerId, index ] ) );
}

/**
 * Queries rendered features on the given layers and returns the topmost one as a click hit.
 * `queryRenderedFeatures` already returns features in topmost-first order, so the first
 * match is the visually highest candidate for this provider.
 *
 * @param map Active MapLibre map.
 * @param geometry Click point or bounding box in screen pixels.
 * @param layerIds Candidate layer ids; ids absent from the style are ignored.
 * @param layerZIndex Paint-order lookup from {@link buildLayerZIndex}.
 * @returns Hit wrapping the topmost feature, or `undefined` when nothing is hit.
 */
export function queryTopmostFeatureHit(
    map: maplibregl.Map,
    geometry: maplibregl.PointLike | [maplibregl.PointLike, maplibregl.PointLike],
    layerIds: readonly string[],
    layerZIndex: ReadonlyMap<string, number>,
): MapClickHit | undefined {
    const existingLayerIds = layerIds.filter( ( layerId ) => map.getLayer( layerId ) !== undefined );

    if ( existingLayerIds.length === 0 ) {
        return undefined;
    }

    const feature = map.queryRenderedFeatures( geometry, { "layers": existingLayerIds } )[0];

    if ( feature === undefined ) {
        return undefined;
    }

    return {
        "zIndex":  layerZIndex.get( feature.layer.id ) ?? -1,
        "payload": feature,
    };
}
